import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { api, listFrom } from '../api';
import type { Person } from '../types';
import { Icon } from './icons';
import { Button, ErrorBlock, Field, LoadingBlock } from './ui';

const consentScope = ['immigration', 'marriage', 'career', 'finance', 'family-planning'] as const;

export function OnboardingGate({ children }: { children: ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();
  const peopleQuery = useQuery({ queryKey: ['people'], queryFn: () => api<unknown>('/people') });
  const consentsQuery = useQuery({
    queryKey: ['consents'],
    queryFn: () => api<unknown>('/consents'),
  });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [attested, setAttested] = useState(false);
  const people = listFrom<Person>(peopleQuery.data, ['people']);
  const consents = listFrom<Record<string, unknown>>(consentsQuery.data, ['consents']);
  const partner = people.find((person) => person.relationship === 'partner');
  const partnerConsent = useMemo(
    () =>
      consents.find(
        (consent) =>
          (consent.personId ?? consent.person_id) === partner?.id &&
          !(consent.revokedAt ?? consent.revoked_at),
      ),
    [consents, partner],
  );
  const setup = useMutation({
    mutationFn: async () => {
      const currentPartner = partner;
      if (!currentPartner)
        return api<Person>('/people', {
          method: 'POST',
          body: {
            displayName: 'Partner',
            relationship: 'partner',
            birthYear: null,
            visibility: 'owner_only',
            consent: {
              scope: consentScope,
              method: 'owner_attestation',
              consentDate: date,
              revocable: true,
            },
          },
        });
      return api('/consents', {
        method: 'POST',
        body: {
          personId: currentPartner.id,
          scope: consentScope,
          method: 'owner_attestation',
          consentDate: date,
          revocable: true,
        },
      });
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['people'] }),
        queryClient.invalidateQueries({ queryKey: ['consents'] }),
      ]),
  });
  if (peopleQuery.isLoading || consentsQuery.isLoading)
    return (
      <div className="gate-loading">
        <LoadingBlock label="Checking consent record" />
      </div>
    );
  if (peopleQuery.isError || consentsQuery.isError)
    return (
      <div className="gate-loading">
        <ErrorBlock
          error={peopleQuery.error ?? consentsQuery.error}
          onRetry={() => {
            void peopleQuery.refetch();
            void consentsQuery.refetch();
          }}
        />
      </div>
    );
  if (partner && partnerConsent) return <>{children}</>;
  return (
    <div className="onboarding-page">
      <div className="onboarding-progress">
        <span className="is-complete">
          <Icon name="check" />
          Owner access
        </span>
        <i />
        <span className="is-current">2</span>
        <strong>Partner consent</strong>
        <i />
        <span>3</span>
        <strong>Workspace ready</strong>
      </div>
      <div className="onboarding-layout">
        <section>
          <p className="eyebrow">One required safeguard</p>
          <h1>Record partner consent before creating their profile.</h1>
          <p className="lead">
            Akshay’s Atlas can hold a full partner profile only after you attest to consent for
            every included scope. Consent remains revocable.
          </p>
          <div className="consent-scope-list">
            {consentScope.map((scope) => (
              <div key={scope}>
                <Icon name="check" size={15} />
                <span>{scope.replace('-', ' ')}</span>
              </div>
            ))}
          </div>
          <div className="onboarding-principle">
            <Icon name="shield" />
            <p>
              <strong>Purpose-limited by design</strong>
              <br />
              Religion is only stored in a consented, legally relevant field. Caste is never stored,
              displayed or scored.
            </p>
          </div>
        </section>
        <form
          className="consent-card"
          onSubmit={(event) => {
            event.preventDefault();
            if (attested && date) setup.mutate();
          }}
        >
          <span className="consent-card__icon">
            <Icon name="profile" />
          </span>
          <h2>Partner profile consent</h2>
          <p>The profile display name will remain “Partner” until explicitly updated.</p>
          <dl>
            <dt>Method</dt>
            <dd>Owner attestation</dd>
            <dt>Revocable</dt>
            <dd>Yes</dd>
            <dt>Profile visibility</dt>
            <dd>Owner only</dd>
          </dl>
          <Field label="Consent date">
            <input
              required
              type="date"
              value={date}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>
          <label className="check-row">
            <input
              type="checkbox"
              checked={attested}
              onChange={(event) => setAttested(event.target.checked)}
            />
            <span>
              I attest that Partner consented to storing a profile for all five scopes shown.
            </span>
          </label>
          {setup.isError ? (
            <p className="form-error" role="alert">
              {setup.error.message}
            </p>
          ) : null}
          <Button type="submit" variant="primary" disabled={!attested || !date || setup.isPending}>
            {setup.isPending ? 'Recording consent…' : 'Record consent & continue'}
            <Icon name="arrow" size={16} />
          </Button>
          <small>You can revoke this consent from Profile & Intake.</small>
        </form>
      </div>
    </div>
  );
}
